import * as React from "react";
import classNames from "classnames";
import {
  FormFieldError,
  InputElementClassUnion,
  InputElementUnion,
  createInputElement,
} from "@graffer-inc/form-schema";
import {
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { TenantProcessResultDocument } from "@graffer-inc/cloud-gov-core";

import { ErrorMessage } from "./../../../../atoms/error-message";
import type { ApplicationDetailContextProps } from "../../../../../application/service/smart-apply/application-detail/application-detail-service";

type TenantProcessResultEditProps = {
  inputElements: InputElementUnion[];
  document?: TenantProcessResultDocument;
  onUpdate: ApplicationDetailContextProps["onUpdateTenantProcessResult"];
  finishEdit: () => void;
};

export const TenantProcessResultEdit: React.FC<
  TenantProcessResultEditProps
> = ({ inputElements, document, onUpdate, finishEdit }) => {
  const [values, setValues] = React.useState<TenantProcessResultDocument>(
    document ?? {},
  );
  const [errors, setErrors] = React.useState<{
    [key: string]: FormFieldError;
  }>({});
  const formSubmissionContext = React.useMemo(
    () => ({
      formSubmissionDate: new Date(),
    }),
    [],
  );
  const hanedleClick = React.useCallback(async () => {
    const isUpdated = await onUpdate(values);
    if (isUpdated) {
      finishEdit();
    }
  }, [onUpdate, values, finishEdit]);
  const [showNotification, setShowNotification] = React.useState<boolean>(true);

  return (
    <>
      {showNotification && (
        <div className="notification is-warning is-light">
          <button
            className="delete"
            onClick={() => setShowNotification(false)}
          />
          個人番号を含む特定個人情報は入力しないでください。
        </div>
      )}
      {inputElements.map((e) => (
        <TenantProcessResultTextInput
          key={e.id}
          element={createInputElement(e)}
          value={values[e.id]}
          error={errors[e.id]}
          setFieldValue={(value) => setValues({ ...values, [e.id]: value })}
          setFieldError={(error) => setErrors({ ...errors, [e.id]: error })}
          formSubmissionContext={formSubmissionContext}
        />
      ))}
      <p className="help has-text-weight-normal mt-5">
        管理情報を保存しても申請者に通知されることはありません。
      </p>
      <div className="level-left buttons mt-2">
        <SecondaryButton onClick={finishEdit} text="キャンセル" />
        <PrimaryButton
          disabled={Object.values(errors).filter((v) => !!v).length > 0}
          onClick={hanedleClick}
          text="保存する"
        />
      </div>
    </>
  );
};

type TenantProcessResultTextInputProps = {
  element: InputElementClassUnion;
  value: string | undefined;
  error: FormFieldError;
  setFieldValue: (value: string) => void;
  setFieldError: (error: FormFieldError) => void;
  formSubmissionContext: { formSubmissionDate: Date };
};

const TenantProcessResultTextInput: React.FC<
  TenantProcessResultTextInputProps
> = ({
  element,
  value,
  error,
  setFieldValue,
  setFieldError,
  formSubmissionContext,
}) => {
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setFieldValue(val);
      const err = element.validate(val, undefined, formSubmissionContext);
      setFieldError(err);
    },
    [setFieldValue, setFieldError, element, formSubmissionContext],
  );
  return (
    <>
      <div className="field" key={element.id}>
        <label className="label" htmlFor={element.id}>
          {element.label.text}
          <p className="help has-text-weight-normal">
            {element.description.text}
          </p>
        </label>
        <div className="control">
          <input
            id={element.id}
            className={classNames("input", {
              "is-danger": !!error,
            })}
            value={value ?? ""}
            name={element.id}
            onChange={handleChange}
          />
        </div>
        <ErrorMessage msg={error} />
      </div>
    </>
  );
};
